import { NgClass } from '@angular/common';
import { Component, ElementRef, Input, NgZone, OnChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ScrollingSizeConfig, TopNavigationConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent } from '@frontend/sports/common/core/feature/scroll-adapter';
import { ResizeObserverService } from '@frontend/sports/common/core/utils/dom';
import { SportsTheme, ThemeInverse } from '@frontend/sports/common/ui/shared';
import { LinkDirective, SportsThemedPillsDirective } from '@frontend/sports/common/ui/sports-themed-pills';
import { AdaptiveLayoutService } from '@frontend/sports/layout/feature';
import { SizedNavigationItem, TopMenuContentItem, TrackNavArea } from '@frontend/sports/navigation/core/feature/models';
import { PopupManager, PopupTypes } from '@frontend/sports/popup/feature';
import { CharsetSizeService } from '@frontend/sports/web/charset-size-data-access';
import { NavigationRouterService } from '@frontend/sports/web/navigation-data-access';
import { DsNotificationBubble } from '@frontend/ui/notification-bubble';
import { DsPill } from '@frontend/ui/pill';
import { MenuAction, MenuActionsService, MenuDisplayMode, MenuItemsService, NativeAppService } from '@frontend/vanilla/core';
import { MenuItemActionDirective, MenuItemBadgeDirective, MenuItemComponent, MenuItemTrackingDirective } from '@frontend/vanilla/features/menus';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import { cloneDeep, sumBy } from 'lodash-es';
import { distinctUntilChanged, map, take } from 'rxjs';

enum PopupType {
    Sports = 'sports',
    ProductMenu = 'product-menu',
}

@Component({
    selector: 'ms-main-items',
    templateUrl: './main-items.component.html',
    imports: [
        ScrollAdapterComponent,
        DsPill,
        MenuItemActionDirective,
        MenuItemTrackingDirective,
        SportsThemedPillsDirective,
        LinkDirective,
        SpeculativeLinkDirective,
        IconCustomComponent,
        DsNotificationBubble,
        MenuItemBadgeDirective,
        NgClass,
        MenuItemComponent,
    ],
})
export class MainItemsComponent implements OnChanges {
    private layoutService = inject(AdaptiveLayoutService);
    private navigationRouter = inject(NavigationRouterService);
    private scrollingConfig = inject(ScrollingSizeConfig);
    private popupManager = inject(PopupManager);
    private menuActionService = inject(MenuActionsService);
    private resizeObserver = inject(ResizeObserverService);
    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private charsetService = inject(CharsetSizeService);
    navigationConfig = inject(TopNavigationConfig);
    private menuItemService = inject(MenuItemsService);
    private strategy = inject(RxStrategyProvider);
    private nativeAppService = inject(NativeAppService);

    @Input() items: TopMenuContentItem[];
    @Input() compactItems: boolean;
    @Input() isEpcotEnabled: boolean;

    tracking = TrackNavArea;
    scrollSize: number;
    filteredItems: TopMenuContentItem[];
    liveCount = '0';
    scrolled = signal(false);
    inverseThemes: SportsTheme[] = ['mgm-dice'];
    themeInverseIndicator: ThemeInverse;

    private menuWidth: number;
    private menuMargin = 24;
    private itemSeparator = 24;
    private itemIcon = 21;
    private itemMargin = 24;
    private itemCompact = 65;
    private isV2Navigation: boolean;

    private ngZone = inject(NgZone);

    private get sportsPopupOpen(): boolean {
        return this.popupManager.isPopupOpen(PopupType.Sports);
    }

    constructor() {
        this.isV2Navigation = !!this.navigationConfig.topNavigationItems?.length;
        this.scrollSize = this.scrollingConfig.topNavScrollSize || 200;
        this.resizeObserver
            .observe(this.elementRef)
            .pipe(
                map((change) => change.contentRect.width),
                distinctUntilChanged(),
                takeUntilDestroyed(),
            )
            .subscribe((width) => {
                this.menuWidth = width;
                this.fit();
            });

        this.themeInverseIndicator = {
            'mgm-dice': { isInverse: true },
        };
    }

    ngOnChanges(): void {
        this.fit();
    }

    itemClick(event: Event, item: TopMenuContentItem): void {
        if (item.popup && this.isActive(this.tracking.NavTop, item.name)) {
            this.popupManager.close();

            return;
        }

        if (item.popup) {
            event.preventDefault();
            this.strategy
                .schedule(
                    () => {
                        switch (item.popup) {
                            case PopupType.Sports:
                                this.openSportsPopup(item);
                                break;
                            case PopupType.ProductMenu:
                                this.menuActionService.invoke(MenuAction.TOGGLE_PRODUCT_MENU, '', [
                                    undefined,
                                    undefined,
                                    { 'animate-from': 'bottom' },
                                ]);
                                break;
                            default:
                                this.popupManager.open(item.popup as PopupTypes);
                        }
                    },
                    { scope: this, patchZone: this.ngZone },
                )
                .pipe(take(1))
                .subscribe();
        }

        if (item.ccbKey) {
            this.nativeAppService.sendToNative({
                eventName: 'MenuIteamNavigation',
                parameters: { product: TrackNavArea.NavTop, ccbKey: item.ccbKey },
            });
        }
    }

    private openSportsPopup(item: TopMenuContentItem): void {
        if (this.sportsPopupMode) {
            this.popupManager.open(item.popup as PopupTypes);
            this.navigationRouter.setNavigation(item.name);
        } else {
            this.switchPopup();
        }
    }

    isActive(selection: string, itemName: string): boolean {
        return this.menuItemService.isActive(selection, itemName);
    }

    onScrolled(): void {
        this.scrolled.set(true);
    }

    get sportsPopupMode(): boolean {
        return this.layoutService.current.showDesktopAZSports;
    }

    private fit(): void {
        if (!this.items) {
            return;
        }

        this.trySwitchPopup();

        this.filteredItems = cloneDeep(this.items);

        if (this.sportsPopupMode && this.menuWidth) {
            const sizes = this.filteredItems.map((item) => ({ item, width: this.getItemWidth(item) }));
            let sports = sizes.filter((current) => current.item.isSport && current.item.name !== this.navigationRouter.activeNavigation);
            const total = this.menuMargin + sumBy(sizes, (s) => s.width);

            if (this.isV2Navigation) {
                sports = sizes.filter((current) => !current.item.isFixed);
            }

            this.filterNavigation(sports, total);
        }

        this.scrolled.set(false);
    }

    private filterNavigation(navItems: SizedNavigationItem[], total: number): void {
        while (total > this.menuWidth && navItems.length > 0) {
            const lastItem = navItems.pop();

            if (lastItem) {
                total -= lastItem.width;
                this.filteredItems.splice(this.filteredItems.indexOf(lastItem.item), 1);
            }
        }
    }

    private trySwitchPopup(): void {
        if (!this.sportsPopupMode && this.sportsPopupOpen) {
            this.switchPopup();
        }
    }

    private switchPopup(): void {
        this.menuActionService.invoke(MenuAction.TOGGLE_PRODUCT_MENU, '');
        this.popupManager.closeAll();
    }

    private getItemWidth(item: TopMenuContentItem): number {
        if (this.compactItems) {
            return this.itemCompact;
        }

        return (
            this.itemIcon +
            this.itemMargin +
            this.charsetService.getAverageSize() * item.text.length +
            (item.class?.includes('separator') ? this.itemSeparator : 0)
        );
    }

    protected readonly MenuDisplayMode = MenuDisplayMode;
}
